import { useEffect, useState } from "react";
import { getNodes, getNodeSets, getProfilerMetadata } from "../api/quontxApi";
import SidePanel from "../components/SidePanel";
import RadarPlotFilters from "../components/RadarPlotFilters";
import ScaterPlotFilters from "../components/ScatterPlotFilters";
import DumbbellPlotFilters from "../components/DumbbellPlotFilters";
import ProfileComparatorFilters from "../components/ProfileComparatorFilters";
import LinePlotFilters from "../components/LinePlotFilters";
import { getPaginatedData } from "../utils";
import ProfileFilter from "../components/ProfileFilters";

const NodeAnalysisPage = () => {
  const [collapsed, setCollapsed] = useState(true);
  const [activeChart, setActiveChart] = useState("radar");
  const [displayData, setDisplayData] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const [entities, setEntities] = useState([]);
  const [proxies, setProxies] = useState([]);
  const [domains, setDomains] = useState([]);
  const [timestampBuckets, setTimestampBuckets] = useState([]);

  const loadNodes = async () => {
    const nodes = await getPaginatedData(getNodes, {
      itemsPerPage: 100,
      type: "NAMED_ENTITY",
    });
    setEntities(
      nodes.data
        .filter((entity) => !entity.name.includes(" Proxy"))
        .sort((a, b) => {
          if (a.name > b.name) return 1;
          if (a.name < b.name) return -1;
          return 0;
        })
    );

    setProxies(
      nodes.data
        .filter((entity) => entity.name.includes(" Proxy"))
        .sort((a, b) => {
          if (a.name > b.name) return 1;
          if (a.name < b.name) return -1;
          return 0;
        })
    );
  };

  const loadNodeSets = async () => {
    const nodeSets = await getPaginatedData(getNodeSets, {
      itemsPerPage: 100,
    });

    setDomains(
      nodeSets.data.sort((a, b) => {
        if (a.description > b.description) return 1;
        if (a.description < b.description) return -1;
        return 0;
      })
    );
  };

  const loadTimestampBuckets = async () => {
    const profilerMetadata = await getPaginatedData(getProfilerMetadata, {
      itemsPerPage: 100,
    });

    const timestampBuckets = [];
    profilerMetadata.data.forEach((nodeData) => {
      // Only keep the timestamps of proxies for now
      if (nodeData.metadata.corpora.name.includes(" Proxy")) {
        timestampBuckets.push(...nodeData.metadata.corpora.time_intervals);
      }
    });

    setTimestampBuckets(timestampBuckets);
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await loadNodes();
      await loadNodeSets();
      await loadTimestampBuckets();
      setIsLoading(false);
    })();
  }, []);

  return (
    <div className="flex relative">
      <SidePanel
        collapseCallback={() => setCollapsed(!collapsed)}
        collapsed={collapsed}
        data={displayData}
      />
      <div className="grow">
        <div className="p-6 pb-0">
          <h1 className="mb-6 text-center">Data Visualisation</h1>
          <div className="w-full flex flex-col items-center content-center overflow-y-auto custom-scrollbar h-[83vh]">
            {entities && domains && (
              <div className="flex flex-col items-center content-center gap-6 w-full">
                <div className="flex gap-4 items-center content-center">
                  <button
                    onClick={() => setActiveChart("radar")}
                    className={`${
                      activeChart === "radar" ? "bg-blue-800" : "bg-blue-500"
                    }  text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200`}
                  >
                    Radar
                  </button>
                  <button
                    onClick={() => setActiveChart("scatter")}
                    className={`${
                      activeChart === "scatter" ? "bg-blue-800" : "bg-blue-500"
                    }  text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200`}
                  >
                    Scatter
                  </button>
                  <button
                    onClick={() => setActiveChart("gauge")}
                    className={`${
                      activeChart === "gauge" ? "bg-blue-800" : "bg-blue-500"
                    }  text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200`}
                  >
                    Gauge
                  </button>
                  <button
                    onClick={() => setActiveChart("dumbbell")}
                    className={`${
                      activeChart === "dumbbell" ? "bg-blue-800" : "bg-blue-500"
                    } disabled:opacity-[0.4] text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200`}
                  >
                    Dumbbell
                  </button>
                  <button
                    onClick={() => setActiveChart("line")}
                    className={`${
                      activeChart === "line" ? "bg-blue-800" : "bg-blue-500"
                    } disabled:opacity-[0.4] text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200`}
                  >
                    Line
                  </button>
                  <button
                    onClick={() => setActiveChart("profile")}
                    className={`${
                      activeChart === "profile" ? "bg-blue-800" : "bg-blue-500"
                    } disabled:opacity-[0.4] text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200`}
                  >
                    Profile
                  </button>
                </div>
                <div className="w-full">
                  {activeChart === "radar" && (
                    <RadarPlotFilters
                      propagnateData={(value) => setDisplayData(value)}
                      timestampBuckets={timestampBuckets}
                      domains={domains}
                      entities={entities}
                      proxies={proxies}
                      isLoading={isLoading}
                    />
                  )}
                  {activeChart === "scatter" && (
                    <ScaterPlotFilters
                      propagnateData={(value) => setDisplayData(value)}
                      timestampBuckets={timestampBuckets}
                      domains={domains}
                      entities={entities}
                      proxies={proxies}
                      isLoading={isLoading}
                    />
                  )}
                  {activeChart === "gauge" && (
                    <ProfileComparatorFilters
                      propagnateData={(value) => setDisplayData(value)}
                      entities={entities}
                      proxies={proxies}
                      setDisplayData={setDisplayData}
                      isLoading={isLoading}
                    />
                  )}
                  {activeChart === "dumbbell" && (
                    <DumbbellPlotFilters
                      propagnateData={(value) => setDisplayData(value)}
                      timestampBuckets={timestampBuckets}
                      domains={domains}
                      proxies={proxies}
                      isLoading={isLoading}
                    />
                  )}
                  {activeChart === "line" && (
                    <LinePlotFilters
                      propagnateData={(value) => setDisplayData(value)}
                      timestampBuckets={timestampBuckets}
                      domains={domains}
                      entities={entities}
                      proxies={proxies}
                      isLoading={isLoading}
                    />
                  )}
                  {activeChart === "profile" && (
                    <ProfileFilter
                      propagnateData={(value) => setDisplayData(value)}
                      entities={entities}
                      proxies={proxies}
                      isLoading={isLoading}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NodeAnalysisPage;
